import * as d3 from 'd3'
import {
  type SankeyLink,
  type SankeyNode,
  sankey,
  sankeyCenter,
  sankeyJustify,
  sankeyLeft,
  sankeyLinkHorizontal,
  sankeyRight,
} from 'd3-sankey'
import * as React from 'react'

import { cn } from '@/utils'
import { CHART_COLORS } from './Constants'
import { ChartControls } from './Controls'
import { useChartBase } from './Hooks'
import { ChartTooltip } from './Tooltip'
import {
  collapseSankeyToFit,
  maxSankeyColumnNodes,
} from './utils/collapseSankey'
import type { DateRangeType, Filter, SchemaField } from './utils/exportChart'
import { formatChartValue } from './utils/formatters'

// `name` is the unique key links point at; `label` is the text shown.
interface SankeyNodeData {
  name: string
  label?: string
  color?: string
}

interface SankeyLinkData {
  source: number
  target: number
  value: number
}

interface SankeyChartData {
  nodes: SankeyNodeData[]
  links: SankeyLinkData[]
}

interface SankeyChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data?: SankeyChartData
  className?: string
  width?: string | number
  height?: number
  margin?: { top: number; right: number; bottom: number; left: number }
  nodeWidth?: number
  nodePadding?: number
  align?: 'justify' | 'left' | 'right' | 'center'
  color?: string
  onChartReady?: (api: {
    containerRef: React.RefObject<HTMLDivElement>
    isFullscreen: boolean
    toggleFullscreen: () => Promise<void>
  }) => void
  projectName?: string
  title?: string
  filters?: Filter[]
  schema?: SchemaField[]
  dateRange?: DateRangeType
  filename?: string
  showHoverControls?: boolean
  valueFormat?: 'currency' | 'number'
  collapseToFit?: boolean
}

type HoveredNode = SankeyNode<SankeyNodeData, SankeyLinkData> & { type: 'node' }
type HoveredLink = SankeyLink<SankeyNodeData, SankeyLinkData> & { type: 'link' }
type HoveredData = HoveredNode | HoveredLink | null
type LaidOutNode = SankeyNode<SankeyNodeData, SankeyLinkData>

const displayName = (node: SankeyNodeData) => node.label ?? node.name

const linkEnds = (link: SankeyLink<SankeyNodeData, SankeyLinkData>) => ({
  source: link.source as LaidOutNode,
  target: link.target as LaidOutNode,
})

const SankeyChart = React.forwardRef<HTMLDivElement, SankeyChartProps>(
  (
    {
      data = { nodes: [], links: [] },
      className,
      width = '100%',
      height = 400,
      margin = { top: 20, right: 20, bottom: 20, left: 20 },
      nodeWidth = 20,
      nodePadding = 20,
      align = 'justify',
      color = 'currentColor',
      onChartReady,
      projectName,
      title,
      filters,
      schema,
      dateRange,
      filename = 'chart-export.png',
      showHoverControls = false,
      valueFormat = 'currency',
      collapseToFit = false,
      ...props
    },
    _ref,
  ) => {
    const {
      containerRef,
      dimensions,
      isFullscreen,
      toggleFullscreen,
      fullscreenError: error,
      fullscreenSupported: isSupported,
    } = useChartBase(onChartReady)
    const [hoveredData, setHoveredData] = React.useState<HoveredData>(null)

    const alignMap: Record<string, typeof sankeyJustify> = {
      justify: sankeyJustify,
      left: sankeyLeft,
      right: sankeyRight,
      center: sankeyCenter,
    }

    const { nodes, links } = React.useMemo(() => {
      if (dimensions.width <= 0 || dimensions.height <= 0) {
        return {
          nodes: [] as SankeyNode<SankeyNodeData, SankeyLinkData>[],
          links: [] as SankeyLink<SankeyNodeData, SankeyLinkData>[],
        }
      }

      if (!data || !data.nodes || !data.links || data.nodes.length === 0) {
        return {
          nodes: [] as SankeyNode<SankeyNodeData, SankeyLinkData>[],
          links: [] as SankeyLink<SankeyNodeData, SankeyLinkData>[],
        }
      }

      const sankeyGenerator = sankey<SankeyNodeData, SankeyLinkData>()
        .nodeWidth(nodeWidth)
        .nodePadding(nodePadding)
        .extent([
          [margin.left, margin.top],
          [dimensions.width - margin.right, dimensions.height - margin.bottom],
        ])
        .nodeAlign(alignMap[align] || sankeyJustify)

      // When asked to fit the height, bundle the smallest recipients into an
      // "Other" node so the busiest column's labels don't collide. Reacts to
      // the measured height, so resizing the window re-collapses live.
      const fitted = collapseToFit
        ? collapseSankeyToFit(
            data,
            maxSankeyColumnNodes(dimensions.height, margin.top + margin.bottom),
          )
        : data

      const nodesCopy = fitted.nodes.map((d: SankeyNodeData) => ({ ...d }))
      const linksCopy = fitted.links.map((d: SankeyLinkData) => ({ ...d }))

      try {
        return sankeyGenerator({
          nodes: nodesCopy,
          links: linksCopy,
        })
      } catch (e) {
        console.error('Sankey generation failed:', e)
        return {
          nodes: [] as SankeyNode<SankeyNodeData, SankeyLinkData>[],
          links: [] as SankeyLink<SankeyNodeData, SankeyLinkData>[],
        }
      }
    }, [data, dimensions, margin, nodeWidth, nodePadding, align, collapseToFit])

    const colorScale = React.useMemo(() => d3.scaleOrdinal(CHART_COLORS), [])

    return (
      <div
        ref={containerRef}
        className={cn('w-full h-full relative group', className)}
        {...props}
      >
        {nodes.length > 0 && (
          <svg
            role="img"
            aria-label="Sankey chart"
            width={dimensions.width}
            height={dimensions.height}
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
            className="overflow-visible"
          >
            <g>
              {links.map(
                (
                  link: SankeyLink<SankeyNodeData, SankeyLinkData>,
                  i: number,
                ) => {
                  const gradientId = `gradient-${i}`
                  const sourceNode = link.source as SankeyNode<
                    SankeyNodeData,
                    SankeyLinkData
                  >
                  const targetNode = link.target as SankeyNode<
                    SankeyNodeData,
                    SankeyLinkData
                  >
                  const sourceColor =
                    sourceNode.color || colorScale(sourceNode.name) || color
                  const targetColor =
                    targetNode.color || colorScale(targetNode.name) || color

                  return (
                    <React.Fragment key={`link-${i}`}>
                      <linearGradient
                        id={gradientId}
                        gradientUnits="userSpaceOnUse"
                        x1={sourceNode.x1 || 0}
                        x2={targetNode.x0 || 0}
                      >
                        <stop offset="0%" stopColor={sourceColor as string} />
                        <stop offset="100%" stopColor={targetColor as string} />
                      </linearGradient>
                      <path
                        d={sankeyLinkHorizontal()(link) || undefined}
                        stroke={`url(#${gradientId})`}
                        strokeWidth={Math.max(1, link.width || 0)}
                        strokeOpacity={0.4}
                        fill="none"
                        className="transition-opacity duration-200 cursor-pointer hover:stroke-opacity-70"
                        onPointerEnter={() =>
                          setHoveredData({
                            ...link,
                            type: 'link',
                          } as HoveredLink)
                        }
                        onPointerLeave={() => setHoveredData(null)}
                      />
                    </React.Fragment>
                  )
                },
              )}
            </g>

            <g>
              {nodes.map(
                (
                  node: SankeyNode<SankeyNodeData, SankeyLinkData>,
                  i: number,
                ) => (
                  <rect
                    key={`node-${i}`}
                    x={node.x0}
                    y={node.y0}
                    width={(node.x1 || 0) - (node.x0 || 0)}
                    height={(node.y1 || 0) - (node.y0 || 0)}
                    fill={
                      (node.color || colorScale(node.name) || color) as string
                    }
                    className="cursor-pointer hover:opacity-80 transition-opacity duration-200"
                    onPointerEnter={() =>
                      setHoveredData({ ...node, type: 'node' } as HoveredNode)
                    }
                    onPointerLeave={() => setHoveredData(null)}
                  />
                ),
              )}
            </g>

            <g style={{ pointerEvents: 'none' }}>
              {nodes.map(
                (
                  node: SankeyNode<SankeyNodeData, SankeyLinkData>,
                  i: number,
                ) => {
                  const labelX =
                    (node.x0 || 0) < dimensions.width / 2
                      ? (node.x1 || 0) + 6
                      : (node.x0 || 0) - 6
                  const anchor =
                    (node.x0 || 0) < dimensions.width / 2 ? 'start' : 'end'
                  return (
                    <text
                      key={`label-${i}`}
                      x={labelX}
                      y={((node.y1 || 0) + (node.y0 || 0)) / 2}
                      dy="-0.1em"
                      textAnchor={anchor}
                      fontSize={isFullscreen ? '14' : '10'}
                      fontWeight="500"
                      fill="currentColor"
                      className="select-none"
                    >
                      {displayName(node)}
                      <tspan
                        x={labelX}
                        dy="1.1em"
                        fontWeight="500"
                        fillOpacity={0.85}
                      >
                        {formatChartValue(node.value ?? 0, valueFormat)}
                      </tspan>
                    </text>
                  )
                },
              )}
            </g>
          </svg>
        )}

        {showHoverControls && !onChartReady && (
          <ChartControls
            containerRef={containerRef}
            isFullscreen={isFullscreen}
            toggleFullscreen={toggleFullscreen}
            fullscreenError={error}
            fullscreenSupported={isSupported}
            filename={filename}
            projectName={projectName}
            title={title}
            filters={filters}
            schema={schema}
            dateRange={dateRange}
          />
        )}

        <ChartTooltip
          active={!!hoveredData}
          position={
            hoveredData
              ? {
                  x:
                    hoveredData.type === 'node'
                      ? ((hoveredData.x0 || 0) + (hoveredData.x1 || 0)) / 2
                      : (((
                          hoveredData.source as SankeyNode<
                            SankeyNodeData,
                            SankeyLinkData
                          >
                        ).x1 || 0) +
                          ((
                            hoveredData.target as SankeyNode<
                              SankeyNodeData,
                              SankeyLinkData
                            >
                          ).x0 || 0)) /
                        2,
                  y:
                    hoveredData.type === 'node'
                      ? ((hoveredData.y0 || 0) + (hoveredData.y1 || 0)) / 2
                      : hoveredData.y0 || 0,
                }
              : undefined
          }
        >
          {hoveredData && (
            <>
              <div className="font-semibold">
                {hoveredData.type === 'node'
                  ? displayName(hoveredData)
                  : `${displayName(linkEnds(hoveredData).source)} → ${displayName(linkEnds(hoveredData).target)}`}
              </div>
              <div className="text-muted-foreground opacity-80">
                {formatChartValue(hoveredData.value ?? 0, valueFormat)}
              </div>
            </>
          )}
        </ChartTooltip>
      </div>
    )
  },
)
SankeyChart.displayName = 'SankeyChart'

export { SankeyChart }
